/* global BigInt */
import { useEffect, useRef, useState } from "react";
import { errorText } from "./errors";
export const RECEIPT_REFRESH_MS = 30000;
const empty = { rows: [], error: "", loading: false, block: null, updatedAt: null };
// Receipt reads are independent of sale/DEX health. Values come from one chain snapshot,
// used to anchor the separate live vesting display.
export function useBondReceipts(service, account, refresh, confirmation = null) {
  const [snapshot, setSnapshot] = useState(empty);
  const lastConfirmation = useRef(null);
  useEffect(() => {
    if (!service || !account) { setSnapshot(empty); return; }
    let active = true, reading = false, retryAt = 0, failures = 0;
    let catchupTimer, queued = false, catchupAttempts = 0;
    const isConfirmation = confirmation && confirmation.account.toLowerCase() === account.toLowerCase()
      && lastConfirmation.current !== confirmation;
    if (isConfirmation) lastConfirmation.current = confirmation;
    const scheduleCatchup = () => {
      clearTimeout(catchupTimer);
      catchupTimer = setTimeout(() => read(true), 2000);
    };
    const read = async (urgent = false) => {
      if (!active || document.visibilityState === "hidden") return;
      if (reading) { if (urgent) queued = true; return; }
      if (!urgent && Date.now() < retryAt) return;
      reading = true;
      setSnapshot(previous => ({ ...(previous.service === service && previous.account === account ? previous : empty), service, account, loading: true }));
      try {
        const block = await service.web3.eth.getBlock("latest");
        if (service.lastConfirmedBlock && BigInt(block.number) < BigInt(service.lastConfirmedBlock))
          throw Object.assign(new Error("Transaction confirmed. Waiting for the read network to catch up; your balances will refresh automatically."), { behindConfirmation: true });
        const rows = await service.bonds(account, block.number);
        failures = 0; retryAt = 0;
        if (active) setSnapshot({ service, account, rows, error: "", loading: false, block: rows[0]?.snapshotBlock ?? block.number, timestamp: block.timestamp, receivedAt: performance.now(), updatedAt: Date.now() });
      } catch (error) {
        if (error.behindConfirmation && catchupAttempts++ < 4) scheduleCatchup();
        failures++;
        retryAt = Date.now() + Math.min(120000, RECEIPT_REFRESH_MS * 2 ** (failures - 1));
        if (active) setSnapshot(previous => ({ ...previous, service, account, error: errorText(error), loading: false }));
      } finally {
        reading = false;
        if (queued && active) { queued = false; scheduleCatchup(); }
      }
    };
    read();
    if (isConfirmation) scheduleCatchup();
    const timer = setInterval(read, RECEIPT_REFRESH_MS);
    const visible = () => { if (document.visibilityState === "visible") read(); };
    const focused = () => read();
    window.addEventListener("focus", focused);
    document.addEventListener("visibilitychange", visible);
    return () => {
      active = false;
      clearInterval(timer);
      clearTimeout(catchupTimer);
      window.removeEventListener("focus", focused);
      document.removeEventListener("visibilitychange", visible);
    };
  }, [service, account, refresh, confirmation]);
  return snapshot.service === service && snapshot.account === account ? snapshot : { ...empty, loading: Boolean(service && account) };
}
